import {
  Armchair,
  Boxes,
  ChefHat,
  ClipboardList,
  LayoutGrid,
  Printer,
  Ruler,
  ScrollText,
  Soup,
  Table2,
  Warehouse,
} from 'lucide-react';
import { ReactNode } from 'react';

import { RestaurantOnboardingStepKey } from '../../../types/restaurant/RestaurantOnboardingTypes';

export interface OnboardingStepContent {
  title: string;
  /** Para qué sirve este paso. */
  description: string;
  /** Consejo / qué configurar primero. */
  hint?: string;
  route: string;
  icon: ReactNode;
}

export const ONBOARDING_STEP_CONTENT: Record<
  RestaurantOnboardingStepKey,
  OnboardingStepContent
> = {
  DINING_ROOMS: {
    title: 'Salones',
    description:
      'Son las áreas físicas del local (Salón principal, Terraza, Barra…). Las mesas se agrupan por salón y el personal filtra por salón al tomar órdenes.',
    hint: 'Crea al menos un salón antes que las mesas.',
    route: '/restaurant/operation/dining-rooms',
    icon: <LayoutGrid className="h-6 w-6" />,
  },
  TABLES: {
    title: 'Mesas',
    description:
      'Cada mesa pertenece a un salón y tiene capacidad y forma. Sobre una mesa se abre una orden. El código (M-1, M-2…) lo asigna el sistema.',
    hint: 'Define capacidad y forma; el estado se maneja solo durante el servicio.',
    route: '/restaurant/operation/tables',
    icon: <Table2 className="h-6 w-6" />,
  },
  MENU_CATEGORIES: {
    title: 'Categorías del menú',
    description:
      'Agrupan los platillos y bebidas para el POS (Entradas, Bebidas, Postres…). Cada categoría tiene ícono y color.',
    hint: 'El código se genera del nombre; elige un ícono representativo.',
    route: '/restaurant/menu/categories',
    icon: <ClipboardList className="h-6 w-6" />,
  },
  MENU_ITEMS: {
    title: 'Ítems del menú',
    description:
      'Cada ítem del menú apunta a un producto del catálogo y a una categoría. El precio y la imagen vienen del producto; el código se genera de la categoría (BEB-001…).',
    hint: 'Necesitas productos en el catálogo y al menos una categoría de menú.',
    route: '/restaurant/menu/items',
    icon: <Soup className="h-6 w-6" />,
  },
  PREP_STATIONS: {
    title: 'Estaciones de preparación',
    description:
      'Cocina caliente, Barra, Postres… A cada ítem del menú se le asigna una estación; la comanda se enruta e imprime por estación.',
    hint: 'Opcional pero recomendado si tienes más de un punto de preparación.',
    route: '/restaurant/settings/preparation-stations',
    icon: <ChefHat className="h-6 w-6" />,
  },
  PRINTERS: {
    title: 'Impresoras',
    description:
      'Impresoras de comanda (por estación) y de cuenta. Definen a dónde salen los tickets al enviar a cocina y al cobrar.',
    hint: 'Asocia cada impresora de comanda a su estación.',
    route: '/restaurant/settings/printers',
    icon: <Printer className="h-6 w-6" />,
  },
  RAW_MATERIAL_CATEGORIES: {
    title: 'Categorías de materia prima',
    description:
      'Agrupan los insumos (Carnes, Lácteos, Verduras, Empaques…). Solo se usan si vas a controlar inventario de insumos y recetas.',
    hint: 'Paso opcional: sáltalo si no llevas inventario de insumos.',
    route: '/restaurant/inventory/categories',
    icon: <Boxes className="h-6 w-6" />,
  },
  RAW_MATERIALS: {
    title: 'Materia prima',
    description:
      'El catálogo de insumos: nombre, unidad de medida, categoría, proveedor y costo. Es la base de las recetas y del inventario.',
    hint: 'Define la unidad base con la que llevarás la existencia.',
    route: '/restaurant/inventory/raw-materials',
    icon: <Warehouse className="h-6 w-6" />,
  },
  RAW_MATERIAL_INVENTORY: {
    title: 'Inventario de materia prima',
    description:
      'La existencia actual por sucursal de cada insumo, con stock mínimo y costo promedio. Aquí registras las existencias iniciales.',
    hint: 'Carga las cantidades con las que arrancas la operación.',
    route: '/restaurant/inventory/stock',
    icon: <Warehouse className="h-6 w-6" />,
  },
  UNIT_CONVERSIONS: {
    title: 'Conversiones de unidad',
    description:
      'Equivalencias entre unidades (1 kg = 1000 g, 1 caja = 24 u). Permiten que una receta pida gramos aunque el inventario esté en kilos.',
    hint: 'Configúralas antes de las recetas si mezclas unidades.',
    route: '/restaurant/inventory/unit-conversions',
    icon: <Ruler className="h-6 w-6" />,
  },
  RECIPES: {
    title: 'Recetas',
    description:
      'La lista de insumos y cantidades de cada platillo. Al vender o preparar, el sistema descuenta esos insumos del inventario según el momento configurado.',
    hint: 'Necesitas ítems del menú y materia prima ya creados.',
    route: '/restaurant/menu/recipes',
    icon: <ScrollText className="h-6 w-6" />,
  },
};

// ---------------------------------------------------------------------------
// "Cómo se usa" — flujo operativo del día a día
// ---------------------------------------------------------------------------

export interface OnboardingUsageSlide {
  title: string;
  body: string;
  icon: ReactNode;
}

export const ONBOARDING_USAGE_SLIDES: OnboardingUsageSlide[] = [
  {
    title: 'Abrir una orden',
    body:
      'En Órdenes eliges un salón y una mesa libre, asignas mesero y cliente, y agregas ítems del menú y combos. Puedes editar cantidades mientras la orden está OPEN.',
    icon: <ClipboardList className="h-6 w-6" />,
  },
  {
    title: 'Enviar a cocina',
    body:
      'Al confirmar la orden se genera la comanda y se imprime por estación de preparación. Desde ese momento la orden pasa a cocina y ya no se puede cancelar: debe cobrarse.',
    icon: <ChefHat className="h-6 w-6" />,
  },
  {
    title: 'Preparación y listo',
    body:
      'En Cocina, cada estación marca los ítems como en preparación y listos. Según la configuración de inventario, el descuento de insumos ocurre al iniciar, al finalizar o al facturar.',
    icon: <Soup className="h-6 w-6" />,
  },
  {
    title: 'Cobrar',
    body:
      'Cuando la orden está lista se envía a cobro: se crea una venta normal que continúa en caja. Puedes dividir la cuenta en varias subcuentas y cobrar cada una por separado.',
    icon: <ScrollText className="h-6 w-6" />,
  },
  {
    title: 'Operaciones sobre la orden',
    body:
      'Mover de mesa, mover productos a otra orden, fusionar dos órdenes o dividir la cuenta. Todo se puede hacer antes o después de enviar a cocina sin descuadrar el inventario.',
    icon: <Table2 className="h-6 w-6" />,
  },
  {
    title: 'Inventario y producción',
    body:
      'Las recetas en modo "consumo directo" descuentan insumos con cada venta. Las recetas en modo "lote" se preparan en Producción (materia prima → producto terminado) y la venta solo descuenta el terminado.',
    icon: <Boxes className="h-6 w-6" />,
  },
  {
    title: 'Reservas y merma',
    body:
      'En Reservaciones agendas mesas por franja horaria. En Merma registras el desperdicio de insumos. Movimientos de inventario y Reportes te dan la trazabilidad completa.',
    icon: <Armchair className="h-6 w-6" />,
  },
];
